/**
 * TermsContent — the ONE Terms of Service body (2026-08-23).
 *
 * Extracted from src/pages/Terms.tsx so the same legal text renders on the
 * /terms-of-service page AND inside the onboarding confirm card's in-flow
 * popup (owner directive: agreeing to the terms must never navigate the user
 * out of onboarding; the popup shows the full, selectable, copy-pastable
 * text). Edit the terms HERE — both surfaces render this component.
 */

import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { brandColors, lightTheme } from '../../styles/theme';
import { termsOfServiceDocument } from './termsOfServiceDocument';

const fadeInLeft = keyframes`
  from { opacity: 0; transform: translateX(-30px); }
  to { opacity: 1; transform: translateX(0); }
`;

const SectionTitle = styled.h2`
  font-family: ${lightTheme.fonts.heading};
  font-size: 1.8rem;
  color: ${brandColors.primaryBlack};
  margin-bottom: 1.5rem;
  margin-top: 3rem;
  animation: ${fadeInLeft} 0.6s ease-out;
  animation-delay: 0.2s;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid ${brandColors.border};
`;

const SubSectionTitle = styled.h3`
  font-family: ${lightTheme.fonts.heading};
  font-size: 1.4rem;
  color: ${brandColors.secondaryDark};
  margin-bottom: 1rem;
  margin-top: 2rem;
`;

const TermsText = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: ${brandColors.textSecondary};
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

const TermsList = styled.ul`
  margin-bottom: 1.5rem;
  padding-left: 1.5rem;
`;

const TermsListItem = styled.li`
  font-size: 1rem;
  line-height: 1.6;
  color: ${brandColors.textSecondary};
  margin-bottom: 0.7rem;

  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

const InlineLink = styled(Link)`
  color: ${brandColors.accentGold};
  text-decoration: none;
  font-weight: 500;
  border-bottom: 1px solid rgba(198, 167, 94, 0.3);
  transition: color 0.2s ease, border-bottom-color 0.2s ease;

  &:hover {
    color: ${brandColors.hoverGold};
    border-bottom-color: ${brandColors.hoverGold};
  }
`;

const KeyHighlightsGrid = styled.div`
  display: grid;
  gap: 1.5rem;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  margin: 2.5rem 0 3.5rem;
`;

const KeyHighlightCard = styled.div`
  background: ${brandColors.backgroundSoftWhite};
  border-radius: 16px;
  padding: 1.75rem;
  box-shadow: ${lightTheme.shadows.lg};
  border: 1px solid ${brandColors.border};
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: ${lightTheme.shadows.xl};
    border-color: ${brandColors.accentGold};
  }
`;

const KeyHighlightIcon = styled.span`
  font-size: 1.6rem;
`;

const KeyHighlightTitle = styled.h3`
  font-family: ${lightTheme.fonts.heading};
  font-size: 1.1rem;
  font-weight: 600;
  color: ${brandColors.primaryBlack};
  text-transform: lowercase;
  margin: 0;
`;

const KeyHighlightText = styled.p`
  font-size: 0.95rem;
  line-height: 1.6;
  color: ${brandColors.textSecondary};
  margin: 0;
`;

const TermsOrderedList = styled.ol`
  margin-bottom: 1.5rem;
  padding-left: 1.5rem;
  counter-reset: item;

  li {
    font-size: 1rem;
    line-height: 1.6;
    color: ${brandColors.textSecondary};
    margin-bottom: 0.7rem;

    @media (max-width: 768px) {
      font-size: 0.95rem;
    }
  }
`;

/** A link to another page of this site: this page's gold router link. */
const PageLink: React.FC<{ to: string; children?: React.ReactNode }> = ({ to, children }) => (
  <InlineLink to={to}>{children}</InlineLink>
);

/** An item of a numbered list: plain, since TermsOrderedList styles its items itself. */
const OrderedListItem: React.FC<{ children?: React.ReactNode }> = ({ children }) => <li>{children}</li>;

/** A second address inside a section, lifted above it so the section's title shows under the header. */
const Anchor: React.FC<{ id: string }> = ({ id }) => (
  <span id={id} style={{ display: 'block', position: 'relative', top: '-120px' }} aria-hidden="true" />
);

/** An email address, in the words' own colour and underlined. */
const MailLink: React.FC<{ href: string; children?: React.ReactNode }> = ({ href, children }) => (
  <a href={href} style={{ color: 'inherit', textDecoration: 'underline' }}>{children}</a>
);

/** The terms, drawn in these parts (the words live in ./termsOfServiceDocument.tsx). */
const TERMS = termsOfServiceDocument({
  Text: TermsText,
  List: TermsList,
  ListItem: TermsListItem,
  OrderedList: TermsOrderedList,
  OrderedListItem,
  SubTitle: SubSectionTitle,
  PageLink,
  OutLink: MailLink,
  Anchor,
  Highlights: KeyHighlightsGrid,
  Highlight: KeyHighlightCard,
  HighlightIcon: KeyHighlightIcon,
  HighlightTitle: KeyHighlightTitle,
  HighlightText: KeyHighlightText,
});

export const TermsContent: React.FC = () => (
  <>
    {TERMS.intro}
    {TERMS.sections.map((section) => (
      <React.Fragment key={section.id}>
        <SectionTitle id={section.id}>{section.heading}</SectionTitle>
        {section.body}
      </React.Fragment>
    ))}
  </>
);

export default TermsContent;
